"use client";

import React, { useEffect, useState, useCallback } from "react";
import { toast } from "react-toastify";
import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import DialogContent from "@mui/material/DialogContent";
import useMediaQuery from "@mui/material/useMediaQuery";
import { useTheme } from "@mui/material/styles";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import stlyes from "./PWAInstallPrompt.module.css";
import { InstallIcon } from "./InstallIcon";
import AppImage from "./takbir-app-img.png";
import AppImageMobile from "./takbir-app-img-mobile.png";
import Image from "next/image";

import MoveToInboxRoundedIcon from "@mui/icons-material/MoveToInboxRounded";
// Utility: detect platform and browser
function getDeviceInfo() {
  if (typeof navigator === "undefined")
    return { deviceType: "unknown", browser: "unknown" };

  const userAgent = navigator.userAgent || navigator.vendor || "";
  const platform = navigator.platform || "";

  // Detect iOS (includes iPhone, iPad, iPod, and iPadOS on MacIntel)
  const isIOS =
    (/iPad|iPhone|iPod/.test(userAgent) && !window.MSStream) ||
    (platform === "MacIntel" &&
      navigator.maxTouchPoints > 1 &&
      !window.navigator.userAgent.includes("Mac OS X"));

  // Detect Android
  const isAndroid = /Android/.test(userAgent);

  // Detect browsers
  const isSafari =
    /Safari/.test(userAgent) && !/Chrome|CriOS|Edg|OPR/.test(userAgent);
  const isChrome = /Chrome|CriOS/.test(userAgent) && !/Edg|OPR/.test(userAgent);
  const isEdge = /Edg/.test(userAgent);
  const isFirefox = /Firefox/.test(userAgent);

  let browser = "unknown";
  if (isSafari) browser = "safari";
  else if (isChrome) browser = "chrome";
  else if (isEdge) browser = "edge";
  else if (isFirefox) browser = "firefox";

  let deviceType = "desktop";
  if (isIOS) deviceType = "ios";
  else if (isAndroid) deviceType = "android";

  return { deviceType, browser, isSafari, isIOS };
}

// Utility: check if PWA is already installed across all browsers
function isPWAAlreadyInstalled() {
  if (typeof window === "undefined") return false;

  // Check for standalone mode (works for most browsers)
  const isStandalone =
    window.matchMedia("(display-mode: standalone)").matches ||
    window.navigator.standalone === true ||
    document.referrer.includes("android-app://");

  if (isStandalone) return true;

  // Additional checks for different browsers
  try {
    // Check for service worker registration (indicates PWA installation)
    if ("serviceWorker" in navigator) {
      // This is a more reliable check for installed PWAs
      const hasServiceWorker = navigator.serviceWorker.controller !== null;
      if (hasServiceWorker) {
        // Check if we're in a PWA context by looking at the URL or referrer
        const isPWAContext =
          window.location.protocol === "https:" &&
          window.location.hostname !== "localhost" &&
          window.location.hostname !== "127.0.0.1" &&
          (document.referrer.includes("android-app://") ||
            window.location.search.includes("source=pwa") ||
            window.location.hash.includes("pwa"));

        if (isPWAContext) return true;
      }
    }

    // Check for PWA-specific indicators in localStorage
    const pwaInstalled = localStorage.getItem("pwa_installed");
    if (pwaInstalled === "true") return true;

    // Check for specific browser installation indicators
    // Chrome/Edge: Check for beforeinstallprompt event suppression
    if (
      window.chrome &&
      window.chrome.runtime &&
      window.chrome.runtime.onConnect
    ) {
      // This indicates Chrome extension or PWA context
      return true;
    }

    // Safari: Check for specific PWA indicators
    if (window.navigator.standalone === true) return true;

    // Firefox: Check for specific PWA context
    if (
      window.navigator.userAgent.includes("Firefox") &&
      window.location.protocol === "https:" &&
      !window.location.hostname.includes("localhost")
    ) {
      // Additional Firefox-specific checks could be added here
    }
  } catch (error) {
    // If any check fails, assume not installed to be safe
    console.warn("PWA installation check failed:", error);
  }

  return false;
}

// Keys for web storage
const SESSION_HIDDEN_KEY = "pwa_install_prompt_hidden_session";
const DISMISSED_KEY = "pwa_install_prompt_dismissed";

export default function PWAInstallPrompt() {
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down("md"));
  const [isVisible, setIsVisible] = useState(false);
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [isCeoModalDone, setIsCeoModalDone] = useState(false);
  const [deviceInfo, setDeviceInfo] = useState({
    deviceType: "unknown",
    browser: "unknown",
  });
  const isLocalhost =
    typeof window !== "undefined" &&
    (window.location.hostname === "localhost" ||
      window.location.hostname === "127.0.0.1" ||
      window.location.hostname === "[::1]");
  const urlParamsBypass =
    typeof window !== "undefined" &&
    new URLSearchParams(window.location.search).get("showPWA") === "1";

  // Guard: don't show if dismissed forever
  const isDismissedForever = () => {
    try {
      // During local development or when explicitly overridden, ignore persisted dismissal
      if (isLocalhost || urlParamsBypass) return false;
      return window.localStorage.getItem(DISMISSED_KEY) === "1";
    } catch (e) {
      return false;
    }
  };

  // Guard: don't show more than once per session
  const isHiddenThisSession = () => {
    try {
      if (urlParamsBypass) return false;
      return window.sessionStorage.getItem(SESSION_HIDDEN_KEY) === "1";
    } catch (e) {
      return false;
    }
  };

  const hideForSession = () => {
    try {
      window.sessionStorage.setItem(SESSION_HIDDEN_KEY, "1");
    } catch (e) {}
  };

  const dismissForever = () => {
    try {
      window.localStorage.setItem(DISMISSED_KEY, "1");
    } catch (e) {}
  };
  const [showCeoModal, setShowCeoModal] = useState(false);

  const maybeShow = useCallback(
    (hasInstallPromptEvent) => {
      if (isDismissedForever() || isHiddenThisSession()) return;
      // Wait until CEO modal has been closed at least once in this session
      if (!isCeoModalDone) return;

      // Check if PWA is already installed across any browser
      if (isPWAAlreadyInstalled()) {
        console.log("PWA already installed, not showing install prompt");
        return;
      }

      const info = getDeviceInfo();
      setDeviceInfo(info);

      // if (!showCeoModal) {
      //   return;
      // }

      // Show for:
      // - iOS always (share/add-to-home instructions)
      // - Android when beforeinstallprompt fires or app not installed
      // - Desktop browsers when beforeinstallprompt fires or for Safari/Edge with manual instructions
      if (info.deviceType === "ios") {
        setIsVisible(true);
        return;
      }

      if (hasInstallPromptEvent) {
        setIsVisible(true);
        return;
      }

      // For desktop Safari and Edge, show manual instructions even without beforeinstallprompt
      if (
        info.deviceType === "desktop" &&
        (info.browser === "safari" ||
          info.browser === "edge" ||
          info.browser === "firefox")
      ) {
        setIsVisible(true);
        return;
      }

      // Desktop Chrome may support beforeinstallprompt; if not fired yet, don't show
    },
    [isCeoModalDone]
  );
  useEffect(() => {
    if (typeof window === "undefined") return;

    // Check if PWA is already installed (comprehensive check)
    if (isPWAAlreadyInstalled()) {
      console.log("PWA already installed, skipping install prompt setup");
      return;
    }

    const isStandalone =
      window.matchMedia("(display-mode: standalone)").matches ||
      window.navigator.standalone === true;
    if (isStandalone) return;

    const info = getDeviceInfo();
    setDeviceInfo(info);

    try {
      if (isLocalhost && window.localStorage.getItem(DISMISSED_KEY) === "1") {
        window.localStorage.removeItem(DISMISSED_KEY);
      }
    } catch (e) {}

    const onBeforeInstallPrompt = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
      // If CEO modal already done, try to show immediately
      if (isCeoModalDone) {
        maybeShow(true);
      }
    };

    window.addEventListener("beforeinstallprompt", onBeforeInstallPrompt);

    // Determine whether CEO modal already completed in a previous visit
    try {
      const ceoSeen = window.localStorage.getItem("showCeoModal");
      if (ceoSeen) setIsCeoModalDone(true);
    } catch (e) {}

    // Listen for CEO modal closing in this session
    const onCeoClosed = () => {
      setIsCeoModalDone(true);
    };
    window.addEventListener("ceoModalClosed", onCeoClosed);

    // ✅ Listen for successful install
    window.addEventListener("appinstalled", () => {
      toast.dismiss();
      toast.success("🎉 Takbir is now installed on your device!");
      setIsVisible(false);
      hideForSession();

      // Mark PWA as installed in localStorage for cross-browser detection
      try {
        localStorage.setItem("pwa_installed", "true");
        console.log("PWA installation marked in localStorage");
      } catch (e) {
        console.warn("Failed to mark PWA as installed:", e);
      }
    });

    if (info.deviceType === "ios") maybeShow(false);
    if (
      info.deviceType === "desktop" &&
      (info.browser === "safari" ||
        info.browser === "edge" ||
        info.browser === "firefox")
    )
      maybeShow(false);

    let fallbackTimerId = null;
    if (
      info.deviceType !== "ios" &&
      info.browser !== "safari" &&
      info.browser !== "edge" &&
      info.browser !== "firefox"
    ) {
      fallbackTimerId = window.setTimeout(() => {
        if (!deferredPrompt) {
          maybeShow(false);
        }
      }, 7000);
    }

    return () => {
      window.removeEventListener("beforeinstallprompt", onBeforeInstallPrompt);
      window.removeEventListener("ceoModalClosed", onCeoClosed);
      if (fallbackTimerId) window.clearTimeout(fallbackTimerId);
    };
  }, [maybeShow]);

  // Show immediately when CEO modal completes or the install prompt arrives
  useEffect(() => {
    if (!isCeoModalDone) return;
    const info = getDeviceInfo();
    const shouldManualShow =
      info.deviceType === "ios" ||
      (info.deviceType === "desktop" &&
        (info.browser === "safari" ||
          info.browser === "edge" ||
          info.browser === "firefox"));
    if (deferredPrompt || shouldManualShow) {
      maybeShow(Boolean(deferredPrompt));
    }
  }, [isCeoModalDone, deferredPrompt, maybeShow]);

  const handleInstallClick = async () => {
    if (!deferredPrompt) return;
    try {
      deferredPrompt.prompt();
      const { outcome } = await deferredPrompt.userChoice;
      if (outcome === "accepted") {
        toast.info("Installing Takbir..."); // Show interim message
        // Mark as installed when user accepts installation
        try {
          localStorage.setItem("pwa_installed", "true");
          console.log("PWA installation accepted, marked in localStorage");
        } catch (e) {
          console.warn("Failed to mark PWA as installed:", e);
        }
      }
      hideForSession();
      setIsVisible(false);
      setDeferredPrompt(null);
    } catch (err) {
      hideForSession();
      setIsVisible(false);
    }
  };

  const handleDismiss = (permanent = false) => {
    if (permanent) dismissForever();
    hideForSession();
    setIsVisible(false);
  };

  if (!isVisible) return null;

  return (
    <div className={stlyes.MdlResponsiveFullScreen}>
      <Dialog
        fullScreen={fullScreen}
        open={isVisible}
        onClick={() => handleDismiss(false)}
        aria-labelledby="responsive-dialog-title"
        className={stlyes.MdFullScreenContainer}
      >
        <DialogContent className={stlyes.ModalBody}>
          <div className={stlyes.ModalBodyContent}>
            <div className={stlyes.AppImgDekstop}>
              <Image src={AppImage} />
            </div>
            <div className={stlyes.AppImgMobile}>
              <Image src={AppImageMobile} />
            </div>

            <div className={stlyes.Details}>
              <div className={stlyes.DetailsTopSection}>
                <img
                  className={stlyes.OrgLogo}
                  src="https://cdn.mytakbir.com/static-assets/logos/takbir-lms-learning-management-system-muslim-logo-3x.png"
                  width={88}
                  height={26}
                />

                <span className={stlyes.ModaCloseIcon}>
                  <CloseRoundedIcon
                    className={stlyes.ModaClose}
                    onClick={() => handleDismiss(false)}
                  />
                </span>
              </div>

              <div className={stlyes.Content}>
                <div
                  className={stlyes.Title}
                  style={{
                    marginTop:
                      deviceInfo.deviceType === "ios" ||
                      deviceInfo.browser !== "chrome"
                        ? 25
                        : deviceInfo.browser === "edge"
                        ? 18
                        : 40,
                  }}
                >
                  Install Takbir App
                </div>
                <div className={stlyes.Description}>
                  {deviceInfo.deviceType === "ios" &&
                    deviceInfo.browser === "safari" && (
                      <div>
                        <p>
                          Install Takbir to your Home Screen for a faster,
                          app-like experience.
                        </p>
                        <ol style={{ paddingLeft: 15 }}>
                          <li>Tap the Share button in Safari’s toolbar.</li>
                          <li>Select Add to Home Screen.</li>
                          <li>Tap Add</li>
                        </ol>
                      </div>
                    )}
                  {deviceInfo.deviceType === "desktop" &&
                    deviceInfo.browser === "safari" && (
                      <div>
                        <p>
                          Install Takbir to your desktop for a faster, app-like
                          experience.
                        </p>
                        <ol style={{ paddingLeft: 15 }}>
                          <li>Click the Share button in Safari's toolbar</li>
                          <li>Choose "Add to Dock" </li>
                          <li>Click Add</li>
                        </ol>
                      </div>
                    )}
                  {deviceInfo.deviceType === "desktop" &&
                    deviceInfo.browser === "edge" && (
                      <div>
                        <p>
                          Install Takbir to your device for a faster, app-like
                          experience.
                        </p>
                        <ol style={{ paddingLeft: 15 }}>
                          <li>Click the Apps icon in Edge's toolbar</li>
                          <li>Click Install</li>
                        </ol>
                      </div>
                    )}
                  {deviceInfo.deviceType === "desktop" &&
                    deviceInfo.browser === "firefox" && (
                      <div>
                        <p>
                          Install Takbir to your device for a faster, app-like
                          experience.
                        </p>
                        <ol style={{ paddingLeft: 15 }}>
                          <li>
                            Look for the{" "}
                            <span
                              style={{
                                display: "inline-flex",
                                alignItems: "center",
                                gap: 4,
                              }}
                            >
                              install icon
                              <span
                                style={{
                                  display: "inline-flex",
                                  verticalAlign: "middle",
                                }}
                              >
                                <MoveToInboxRoundedIcon
                                  fontSize="small"
                                  style={{ marginLeft: 2 }}
                                />
                              </span>
                            </span>{" "}
                            in the address bar.
                          </li>
                          <li>Click the icon.</li>
                        </ol>
                      </div>
                    )}

                  {deviceInfo.deviceType === "android" && (
                    <span>
                      Add Takbir to your device for a faster, app-like
                      experience. Click the Install button below to add takbir
                      to your home screen.
                    </span>
                  )}
                  {deviceInfo.deviceType === "desktop" &&
                    deviceInfo.browser === "chrome" && (
                      <span>
                        Add Takbir to your device for a faster, app-like
                        experience. Click the Install button below to add takbir
                        to your home screen.
                      </span>
                    )}
                </div>
              </div>

              <div className={stlyes.DesktopView}>
                <div className={stlyes.FooterButtonContainer}>
                  <div className={stlyes.FooterButtonContainerInner}>
                    <Button
                      className={stlyes.Cancel}
                      variant="contained"
                      onClick={() => handleDismiss(false)}
                      sx={{
                        padding:
                          deviceInfo.deviceType === "ios" ||
                          (deviceInfo.deviceType === "desktop" &&
                            deviceInfo.browser !== "chrome")
                            ? "16px 20px !important"
                            : "14px 24px !important",
                        height:
                          deviceInfo.deviceType === "ios" ||
                          (deviceInfo.deviceType === "desktop" &&
                            deviceInfo.browser !== "chrome")
                            ? "0px !important"
                            : "48px !important",
                      }}
                    >
                      Not now
                    </Button>
                    {(deviceInfo.deviceType === "android" ||
                      (deviceInfo.deviceType === "desktop" &&
                        deviceInfo.browser === "chrome")) && (
                      <Button
                        className={stlyes.Action}
                        variant="contained"
                        onClick={() => handleInstallClick()}
                        sx={{
                          padding: "14px 24px !important",
                        }}
                      >
                        <InstallIcon /> Install
                      </Button>
                    )}
                  </div>
                </div>
              </div>

              <div className={stlyes.MobileView}>
                <div className={stlyes.FooterButtonContainer}>
                  <div className={stlyes.FooterButtonContainerInner}>
                    {(deviceInfo.deviceType === "android" ||
                      (deviceInfo.deviceType === "desktop" &&
                        deviceInfo.browser === "chrome")) && (
                      <Button
                        className={stlyes.Action}
                        variant="contained"
                        onClick={() => handleInstallClick()}
                      >
                        <InstallIcon /> Install
                      </Button>
                    )}

                    {(deviceInfo.deviceType === "ios" ||
                      (deviceInfo.deviceType === "desktop" &&
                        deviceInfo.browser !== "chrome")) && (
                      <Button
                        className={stlyes.Action}
                        variant="contained"
                        onClick={() => handleDismiss(false)}
                      >
                        Got it
                      </Button>
                    )}

                    <Button
                      className={stlyes.Cancel}
                      variant="contained"
                      onClick={() => handleDismiss(false)}
                    >
                      Not now
                    </Button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
